import { useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { USD, compact, int, magnitude, pct } from '../../lib/format';
import { C, ink, surface, text } from '../../lib/theme';
import type { MotivosDetalhe, MotivoRanking } from '../../lib/types';
import { corDoGrupo } from '../ReasonTagPicker';
import { ChartEmpty, ChartTooltip, Legend, axisStyle, bucketLabel, gridStroke } from './shared';

const card = { ...surface, padding: 18, display: 'flex', flexDirection: 'column' as const, gap: 12, minWidth: 0 };
const titulo = { fontSize: 12, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: text.title };
const sub = { fontSize: 11.5, color: text.muted, lineHeight: 1.45 };

function Qualidade({ d }: { d: MotivosDetalhe }) {
  const q = d.qualidade;
  const total = int(q?.total) || 0;
  const p = (n: unknown) => (total ? (int(n) / total) * 100 : 0);
  const itens = [
    { label: 'Eventos auditados', valor: String(total), nota: USD(q?.valor_total), cor: C.fg },
    { label: 'Sem motivo', valor: pct(p(q?.sem_motivo)), nota: `${int(q?.sem_motivo)} eventos · ${USD(q?.valor_sem_motivo)}`, cor: int(q?.sem_motivo) ? C.churn : C.green },
    { label: 'Motivo genérico', valor: pct(p(q?.genericos)), nota: `${int(q?.genericos)} em "Sem causa clara"`, cor: p(q?.genericos) > 15 ? C.amber : C.green },
    { label: 'Com mais de um motivo', valor: pct(p(q?.multi_motivo)), nota: `${int(q?.multi_motivo)} eventos com tags extras`, cor: C.blue },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12 }}>
      {itens.map((i) => (
        <div key={i.label} style={{ ...surface, padding: '14px 16px' }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: text.muted }}>{i.label}</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: i.cor, fontVariantNumeric: 'tabular-nums', margin: '4px 0 2px' }}>{i.valor}</div>
          <div style={{ fontSize: 11, color: text.body }}>{i.nota}</div>
        </div>
      ))}
    </div>
  );
}

function Ranking({ rows, onPick }: { rows: MotivoRanking[]; onPick: (id: number) => void }) {
  const [verRegras, setVerRegras] = useState(false);
  const reais = rows.filter((r) => !r.is_exception);
  const regras = rows.filter((r) => r.is_exception);
  const lista = verRegras ? regras : reais;
  const max = Math.max(1, ...lista.map((r) => magnitude(r.valor_principal)));
  const totalPrincipal = lista.reduce((s, r) => s + magnitude(r.valor_principal), 0) || 1;
  return (
    <div style={{ ...card, flex: 1.4 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={titulo}>Ranking de motivos</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, padding: 3, borderRadius: 10, border: `1px solid ${ink(0.1)}` }}>
          {[['Perda real', false], ['Regras', true]].map(([lab, v]) => (
            <button key={String(lab)} type="button" onClick={() => setVerRegras(v as boolean)} style={{
              border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 7,
              background: verRegras === v ? ink(0.12) : 'transparent', color: verRegras === v ? C.fg : ink(0.5),
            }}>{lab as string}</button>
          ))}
        </div>
      </div>
      <div style={sub}>Barra = valor como motivo principal (soma igual à perda classificada). “Aparece em” conta também quando o motivo é tag secundária.</div>
      {lista.length === 0 ? <div style={{ height: 120 }}><ChartEmpty text={verRegras ? 'Nenhuma regra usada no período.' : 'Nenhum evento confirmado com motivo no período.'} /></div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {lista.map((r) => {
            const v = magnitude(r.valor_principal);
            const cor = corDoGrupo(r.grupo ?? (r.is_exception ? 'Regras' : null));
            const pres = int(r.eventos_presenca); const prin = int(r.eventos_principal);
            const ch = int(r.churn_presenca); const ct = int(r.contraction_presenca);
            return (
              <button key={r.reason_id} type="button" onClick={() => onPick(r.reason_id)} title="Filtrar a página por este motivo principal" style={{
                border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', font: 'inherit', padding: 0, color: C.fg,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: cor, flex: 'none' }} />
                  <span style={{ fontSize: 12.5, fontWeight: 650, flex: 1, minWidth: 0 }}>{r.label}</span>
                  <span style={{ fontSize: 11, color: text.muted, flex: 'none' }}>aparece em {pres}{pres > prin ? ` (${pres - prin} secundário)` : ''}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 750, minWidth: 92, textAlign: 'right', fontVariantNumeric: 'tabular-nums', flex: 'none' }}>{USD(v)}</span>
                </div>
                <div style={{ height: 7, borderRadius: 99, background: ink(0.06), overflow: 'hidden' }}>
                  <div style={{ width: `${(v / max) * 100}%`, height: '100%', borderRadius: 99, background: cor }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, fontSize: 10.5, color: text.muted }}>
                  <span>{pct((v / totalPrincipal) * 100)} do total</span>
                  {pres > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                      <span style={{ width: 64, height: 5, borderRadius: 99, overflow: 'hidden', display: 'flex', background: ink(0.06) }}>
                        <span style={{ width: `${(ch / pres) * 100}%`, background: C.churn }} />
                        <span style={{ width: `${(ct / pres) * 100}%`, background: C.amber }} />
                      </span>
                      <span style={{ color: C.churn, fontWeight: 700 }}>{ch} churn</span>
                      <span style={{ color: C.amber, fontWeight: 700 }}>{ct} contraction</span>
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Grupos({ d }: { d: MotivosDetalhe }) {
  const reais = d.grupos.filter((g) => !g.is_exception);
  const total = reais.reduce((s, g) => s + magnitude(g.valor), 0) || 1;
  return (
    <div style={{ ...card, flex: 1 }}>
      <span style={titulo}>Por grupo de causa</span>
      <div style={sub}>Perda real pelo motivo principal, agrupada.</div>
      {reais.length === 0 ? <div style={{ height: 120 }}><ChartEmpty /></div> : (
        <>
          <div style={{ display: 'flex', height: 14, borderRadius: 99, overflow: 'hidden', background: ink(0.06) }}>
            {reais.map((g) => <span key={g.grupo} title={`${g.grupo}: ${USD(g.valor)}`} style={{ width: `${(magnitude(g.valor) / total) * 100}%`, background: corDoGrupo(g.grupo) }} />)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {reais.map((g) => (
              <div key={g.grupo} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: corDoGrupo(g.grupo), flex: 'none' }} />
                <span style={{ flex: 1, minWidth: 0, color: ink(0.85) }}>{g.grupo}</span>
                <span style={{ fontSize: 11, color: text.muted }}>{int(g.eventos)} ev</span>
                <span style={{ fontSize: 11.5, color: text.body, minWidth: 46, textAlign: 'right' }}>{pct((magnitude(g.valor) / total) * 100)}</span>
                <span style={{ fontWeight: 750, minWidth: 86, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{USD(g.valor)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Combinacoes({ d, nome }: { d: MotivosDetalhe; nome: (id: number) => { label: string; cor: string } }) {
  return (
    <div style={{ ...card, flex: 1 }}>
      <span style={titulo}>Motivos que aparecem juntos</span>
      <div style={sub}>Pares de tags no mesmo evento. Mostra causas que andam juntas.</div>
      {d.pares.length === 0 ? (
        <div style={{ height: 120 }}><ChartEmpty text="Nenhum evento com mais de um motivo ainda. As combinações aparecem quando a auditoria usar tags extras." /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {d.pares.map((p) => {
            const a = nome(p.a_id); const b = nome(p.b_id);
            const max = Math.max(1, ...d.pares.map((x) => int(x.eventos)));
            return (
              <div key={`${p.a_id}-${p.b_id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                  <span style={{ color: a.cor, fontWeight: 700 }}>●</span><span>{a.label}</span>
                  <span style={{ color: text.muted }}>+</span>
                  <span style={{ color: b.cor, fontWeight: 700 }}>●</span><span>{b.label}</span>
                </span>
                <span style={{ width: 70, height: 6, borderRadius: 99, background: ink(0.06), overflow: 'hidden', flex: 'none' }}>
                  <span style={{ display: 'block', width: `${(int(p.eventos) / max) * 100}%`, height: '100%', background: C.blue }} />
                </span>
                <span style={{ fontWeight: 700, minWidth: 40, textAlign: 'right', flex: 'none' }}>{int(p.eventos)} ev</span>
                <span style={{ minWidth: 80, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: text.body, flex: 'none' }}>{USD(p.valor)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MotivoPorZona({ d, nome, top }: { d: MotivosDetalhe; nome: (id: number) => { label: string; cor: string }; top: number[] }) {
  const zonas = useMemo(() => [...new Set(d.zonas.map((z) => z.zone))].sort(), [d.zonas]);
  const cel = new Map(d.zonas.map((z) => [`${z.reason_id}|${z.zone}`, z]));
  const max = Math.max(1, ...d.zonas.filter((z) => top.includes(z.reason_id)).map((z) => magnitude(z.valor)));
  return (
    <div style={{ ...card, flex: 1 }}>
      <span style={titulo}>Motivo × zona</span>
      <div style={sub}>Onde cada causa concentra a perda (motivo principal).</div>
      {top.length === 0 ? <div style={{ height: 120 }}><ChartEmpty /></div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 4, fontSize: 11.5 }}>
            <thead>
              <tr>
                <th />
                {zonas.map((z) => <th key={z} style={{ fontWeight: 700, color: text.muted, textAlign: 'center', padding: '0 4px' }}>{z}</th>)}
              </tr>
            </thead>
            <tbody>
              {top.map((id) => {
                const n = nome(id);
                return (
                  <tr key={id}>
                    <td style={{ color: ink(0.85), paddingRight: 6, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.label}</td>
                    {zonas.map((z) => {
                      const c = cel.get(`${id}|${z}`); const v = magnitude(c?.valor);
                      const a = v / max;
                      return (
                        <td key={z} title={c ? `${USD(v)} · ${int(c.eventos)} eventos` : 'Sem perda'} style={{
                          textAlign: 'center', borderRadius: 7, padding: '7px 4px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                          background: v ? `color-mix(in srgb, ${C.accent} ${Math.round(12 + a * 60)}%, transparent)` : ink(0.03),
                          color: v ? C.fg : text.disabled,
                        }}>{v ? compact(v) : '·'}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Tendencia({ d, nome, top }: { d: MotivosDetalhe; nome: (id: number) => { label: string; cor: string }; top: number[] }) {
  const ids = top.slice(0, 5);
  const meses = [...new Set(d.tendencia.map((t) => t.mes))].sort();
  const data = meses.map((m) => {
    const row: Record<string, number | string> = { mes: m };
    for (const id of ids) {
      const t = d.tendencia.find((x) => x.mes === m && x.reason_id === id);
      row[String(id)] = magnitude(t?.valor);
    }
    return row;
  });
  return (
    <div style={{ ...card }}>
      <span style={titulo}>Tendência dos 5 maiores motivos</span>
      <div style={sub}>Perda mensal por motivo principal. Ajuste o período no filtro para ver mais meses.</div>
      {meses.length < 2 ? (
        <div style={{ height: 160 }}><ChartEmpty text="Selecione um período com pelo menos dois meses para ver a tendência." /></div>
      ) : (
        <>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                <CartesianGrid stroke={gridStroke} vertical={false} />
                <XAxis dataKey="mes" tick={axisStyle} tickFormatter={(v) => bucketLabel(String(v), 'month')} axisLine={false} tickLine={false} />
                <YAxis tick={axisStyle} tickFormatter={(v) => compact(v)} axisLine={false} tickLine={false} width={48} />
                <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                  <ChartTooltip label={bucketLabel(String(label), 'month')} rows={payload.map((p) => ({
                    name: nome(Number(p.dataKey)).label, value: USD(p.value), color: String(p.color),
                  }))} />
                ) : null} />
                {ids.map((id) => (
                  <Line key={id} type="monotone" dataKey={String(id)} stroke={nome(id).cor} strokeWidth={2.2} dot={{ r: 3 }} isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <Legend items={ids.map((id) => ({ label: nome(id).label, color: nome(id).cor }))} />
        </>
      )}
    </div>
  );
}

/**
 * Zona de motivos da Visão Geral (2026-10-27): motivos em tags. Só leitura
 * dos motivos — nenhum KPI da página muda. Clique num motivo do ranking
 * filtra a página pelo motivo principal (mesmo filtro de antes).
 */
export function MotivosZone({ data, onPickReason }: { data: MotivosDetalhe; onPickReason: (id: number) => void }) {
  const info = useMemo(() => new Map(data.ranking.map((r) => [r.reason_id, r])), [data.ranking]);
  const nome = (id: number) => {
    const r = info.get(id);
    return { label: r?.label ?? `Motivo ${id}`, cor: corDoGrupo(r?.grupo ?? (r?.is_exception ? 'Regras' : null)) };
  };
  const topReais = data.ranking
    .filter((r) => !r.is_exception && magnitude(r.valor_principal) > 0)
    .sort((a, b) => magnitude(b.valor_principal) - magnitude(a.valor_principal))
    .slice(0, 8).map((r) => r.reason_id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Qualidade d={data} />
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Ranking rows={data.ranking} onPick={onPickReason} />
        <Grupos d={data} />
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Combinacoes d={data} nome={nome} />
        <MotivoPorZona d={data} nome={nome} top={topReais} />
      </div>
      <Tendencia d={data} nome={nome} top={topReais} />
    </div>
  );
}
